<template>
  <StaffLayout section="Helpers" showHeader>
    <div>
      <div class="mt-4 font-bold text-xl">Portal</div>
      <BaseButton @click="movePortalClick()" class="block mt-4">
        Move a Portal to a Different Client
      </BaseButton>
      <div class="mt-4 font-bold text-xl">Training</div>
      <BaseButton @click="removeTrainingPermissionClick()" class="block mt-4">
        Remove Training Program Permission from Portal
      </BaseButton>
      <BaseButton @click="addTrainingPermissionClick()" class="block mt-4">
        Add Training Program Permission to Portal
      </BaseButton>
      <div class="mt-4 font-bold text-xl">Misc.</div>
      <BaseButton @click="invalidateCacheClick()" class="block mt-4">
        Invalidate Server Cache
      </BaseButton>
    </div>
    <BasePopup
      class="w-1/5"
      :isVisible="isServerCachePopupVisible"
      title="This will clear the server's cache. Please confirm."
      @close="isServerCachePopupVisible = false"
    >
      <Loading v-if="isPopupLoading" />
      <div
        v-if="popupMessage"
        class="ft-mono py-4 px-8 rounded"
        :class="successful ? 'bg-green-100' : 'bg-red-100'"
      >
        {{ popupMessage }}
      </div>
      <BaseButton
        :class="{ invisible: successful }"
        @click="isServerCachePopupVisible = false"
      >
        Cancel
      </BaseButton>
      <BaseButton
        v-show="!popupMessage"
        class="bg-blue-300 float-right"
        :class="{ invisible: successful }"
        @click="confirmInvalidateCache()"
      >
        Confirm
      </BaseButton>
    </BasePopup>

    <!-- Remove Training Permission – Step 1: Select portal & program -->
    <EditPopup
      entity-name="Training - Remove Permission from all Users in Portal"
      :is-visible="isRemoveTrainingPermissionPopupVisible"
      :is-edit-mode="true"
      :save-error="removeTrainingPermissionError"
      save-button-text="Next"
      @close="isRemoveTrainingPermissionPopupVisible = false"
      @save="goToRemoveTrainingConfirm()"
    >
      <BaseSelect
        label="Portal"
        :options="portalOptions"
        v-model="removeTrainingSelectedPortal"
        :error="errorsRemoveTraining.removeTrainingSelectedPortal"
      />
      <Loading v-if="isTrainingProgramsLoading" />
      <BaseSelect
        v-else
        label="Training Program"
        :options="trainingProgramOptions"
        v-model="removeTrainingSelectedProgram"
        :error="errorsRemoveTraining.removeTrainingSelectedProgram"
      />
    </EditPopup>

    <!-- Remove Training Permission – Step 2: Confirmation -->
    <BasePopup
      class="w-1/3"
      :isVisible="isRemoveTrainingConfirmPopupVisible"
      title="Confirm: Remove Training Program Permission"
      @close="isRemoveTrainingConfirmPopupVisible = false"
    >
      <Loading v-if="isPopupLoading" />
      <div v-if="removeTrainingResultMessage" class="ft-mono py-4 px-8 rounded" :class="removeTrainingSuccess ? 'bg-green-100' : 'bg-red-100'">
        {{ removeTrainingResultMessage }}
      </div>
      <p v-else class="py-4">
        Remove the permission for <strong>{{ selectedProgramName }}</strong> from all users in
        <strong>{{ selectedPortalName }}</strong>?
      </p>
      <BaseButton @click="isRemoveTrainingConfirmPopupVisible = false">Cancel</BaseButton>
      <BaseButton
        v-show="!removeTrainingResultMessage"
        class="bg-red-300 float-right"
        @click="executeRemoveTrainingPermission()"
      >
        Confirm
      </BaseButton>
    </BasePopup>

    <!-- Add Training Permission – Step 1: Select portal & program -->
    <EditPopup
      entity-name="Training - Add To all Users in Portal"
      :is-visible="isAddTrainingPermissionPopupVisible"
      :is-edit-mode="true"
      :save-error="addTrainingPermissionError"
      save-button-text="Next"
      @close="isAddTrainingPermissionPopupVisible = false"
      @save="goToAddTrainingConfirm()"
    >
      <BaseSelect
        label="Portal"
        :options="portalOptions"
        v-model="addTrainingSelectedPortal"
        :error="errorsAddTraining.addTrainingSelectedPortal"
      />
      <Loading v-if="isAddTrainingProgramsLoading" />
      <BaseSelect
        v-else
        label="Training Program"
        :options="addTrainingProgramOptions"
        v-model="addTrainingSelectedProgram"
        :error="errorsAddTraining.addTrainingSelectedProgram"
      />
    </EditPopup>

    <!-- Add Training Permission – Step 2: Confirmation -->
    <BasePopup
      class="w-1/3"
      :isVisible="isAddTrainingConfirmPopupVisible"
      title="Confirm: Add Training Program Permission"
      @close="isAddTrainingConfirmPopupVisible = false"
    >
      <Loading v-if="isPopupLoading" />
      <div v-if="addTrainingResultMessage" class="ft-mono py-4 px-8 rounded" :class="addTrainingSuccess ? 'bg-green-100' : 'bg-red-100'">
        {{ addTrainingResultMessage }}
      </div>
      <p v-else class="py-4">
        Add the permission for <strong>{{ addSelectedProgramName }}</strong> to all users in
        <strong>{{ addSelectedPortalName }}</strong> who don't already have it?
      </p>
      <BaseButton @click="isAddTrainingConfirmPopupVisible = false">Cancel</BaseButton>
      <BaseButton
        v-show="!addTrainingResultMessage"
        class="bg-blue-300 float-right"
        @click="executeAddTrainingPermission()"
      >
        Confirm
      </BaseButton>
    </BasePopup>

    <EditPopup
      entity-name="Portal Client"
      :is-visible="isMovePortalPopupVisible"
      :is-edit-mode="true"
      :save-error="popupMessage"
      @close="isMovePortalPopupVisible = false"
      @save="movePortal()"
    >
      <BaseSelect
        label="Portal"
        :options="portalOptions"
        v-model="selectedPortal"
        :error="errorsMovePortal.selectedPortal"
      />
      <BaseSelect
        label="Move to Client"
        :options="clientOptions"
        v-model="selectedClient"
        :error="errorsMovePortal.selectedClient"
      />
    </EditPopup>
  </StaffLayout>
</template>

<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { useAdminStore } from "../../store/adminStore";
import { useBldgStore } from "../../store/bldgStore";
import { useTrainingStore } from "../../store/trainingStore";

import * as yup from "yup";
import { MovePortalToClientRequest } from "../../store/generatedFetchClient";
const adminStore = useAdminStore();
const bldgStore = useBldgStore();
const trainingStore = useTrainingStore();

const isPopupLoading = ref(false);
const popupMessage = ref("");
const successful = ref(false);

onMounted(loadData);
async function loadData() {
  await bldgStore.getPropertyList();
  await adminStore.getClientList();
}

// #region Invalidate server cache
const isServerCachePopupVisible = ref(false);

function invalidateCacheClick() {
  popupMessage.value = "";
  successful.value = false;

  isServerCachePopupVisible.value = true;
}

async function confirmInvalidateCache() {
  isPopupLoading.value = true;
  popupMessage.value = await adminStore.clearCache();

  if (!popupMessage.value) {
    popupMessage.value = "Successfully cleared server cache.";
    successful.value = true;
  } else {
    successful.value = false;
  }

  isPopupLoading.value = false;
}
// #endregion Invalidate server cache

// #region Move portal to client
const isMovePortalPopupVisible = ref(false);
const portalOptions = computed(() =>
  bldgStore.properties.map((p) => ({
    name: p.name,
    value: p.portalId,
  }))
);
const clientOptions = computed(() =>
  adminStore.clients.map((c) => ({ name: c.name, value: c.clientId }))
);

const { handleSubmit: handleSubmitMovePortal, errors: errorsMovePortal } = useForm({
  validationSchema: yup.object({
    selectedPortal: yup.string().required().label("Portal"),
    selectedClient: yup.string().required().label("Client"),
  }),
});
const { value: selectedPortal } = useField<number | string>("selectedPortal");
const { value: selectedClient } = useField<number | string>("selectedClient");

watch(
  selectedPortal,
  () =>
    (selectedClient.value = bldgStore.properties.find(
      (p) => p.portalId == selectedPortal.value
    )?.clientId)
); // Update selected client to the current portal client

async function movePortalClick() {
  selectedPortal.value = "";
  selectedClient.value = "";

  popupMessage.value = "";
  isMovePortalPopupVisible.value = true;
}

let movePortal = handleSubmitMovePortal(async () => {
  popupMessage.value = "";

  var request = {
    portalId: selectedPortal.value,
    clientId: selectedClient.value,
  } as MovePortalToClientRequest;

  popupMessage.value = await adminStore.movePortalToClient(request);
  if (popupMessage.value) return;

  await bldgStore.getPropertyList(true);
  isMovePortalPopupVisible.value = false;
}, onInvalidSubmit);
// #endregion Move portal to client

// #region Remove training program permission from portal
const isRemoveTrainingPermissionPopupVisible = ref(false);
const isRemoveTrainingConfirmPopupVisible = ref(false);
const isTrainingProgramsLoading = ref(false);
const removeTrainingPermissionError = ref("");
const removeTrainingResultMessage = ref("");
const removeTrainingSuccess = ref(false);

const {
  handleSubmit: handleSubmitRemoveTraining,
  errors: errorsRemoveTraining,
  resetForm: resetRemoveTrainingForm,
} = useForm({
  validationSchema: yup.object({
    removeTrainingSelectedPortal: yup.string().required().label("Portal"),
    removeTrainingSelectedProgram: yup.string().required().label("Training Program"),
  }),
});
const { value: removeTrainingSelectedPortal } = useField<number | string>(
  "removeTrainingSelectedPortal"
);
const { value: removeTrainingSelectedProgram } = useField<number | string>(
  "removeTrainingSelectedProgram"
);

const trainingProgramOptions = computed(() =>
  trainingStore.portalTrainingPrograms.map((tp) => ({
    name: tp.name,
    value: tp.trainingProgramId,
  }))
);

const selectedPortalName = computed(
  () =>
    bldgStore.properties.find((p) => p.portalId == removeTrainingSelectedPortal.value)?.name ?? ""
);
const selectedProgramName = computed(
  () =>
    trainingStore.portalTrainingPrograms.find(
      (tp) => tp.trainingProgramId == removeTrainingSelectedProgram.value
    )?.name ?? ""
);

watch(removeTrainingSelectedPortal, async (newPortalId) => {
  if (!newPortalId) return;
  removeTrainingSelectedProgram.value = "";
  isTrainingProgramsLoading.value = true;
  await trainingStore.getTrainingProgramsByPortal(Number(newPortalId));
  isTrainingProgramsLoading.value = false;
});

function removeTrainingPermissionClick() {
  resetRemoveTrainingForm();
  trainingStore.portalTrainingPrograms = [];
  removeTrainingPermissionError.value = "";
  isRemoveTrainingPermissionPopupVisible.value = true;
}

const goToRemoveTrainingConfirm = handleSubmitRemoveTraining(() => {
  removeTrainingPermissionError.value = "";
  removeTrainingResultMessage.value = "";
  removeTrainingSuccess.value = false;
  isRemoveTrainingPermissionPopupVisible.value = false;
  isRemoveTrainingConfirmPopupVisible.value = true;
}, onInvalidSubmitRemoveTraining);

async function executeRemoveTrainingPermission() {
  isPopupLoading.value = true;
  const result = await trainingStore.bulkRemoveTrainingProgramPermission(
    Number(removeTrainingSelectedPortal.value),
    Number(removeTrainingSelectedProgram.value)
  );
  isPopupLoading.value = false;

  if (result.error) {
    removeTrainingResultMessage.value = result.error;
    removeTrainingSuccess.value = false;
  } else {
    removeTrainingResultMessage.value = `Successfully removed ${result.count} permission${result.count !== 1 ? "s" : ""}.`;
    removeTrainingSuccess.value = true;
  }
}

function onInvalidSubmitRemoveTraining() {
  removeTrainingPermissionError.value = "One or more invalid form inputs.";
}
// #endregion Remove training program permission from portal

// #region Add training program permission to portal
const isAddTrainingPermissionPopupVisible = ref(false);
const isAddTrainingConfirmPopupVisible = ref(false);
const isAddTrainingProgramsLoading = ref(false);
const addTrainingPermissionError = ref("");
const addTrainingResultMessage = ref("");
const addTrainingSuccess = ref(false);

const {
  handleSubmit: handleSubmitAddTraining,
  errors: errorsAddTraining,
  resetForm: resetAddTrainingForm,
} = useForm({
  validationSchema: yup.object({
    addTrainingSelectedPortal: yup.string().required().label("Portal"),
    addTrainingSelectedProgram: yup.string().required().label("Training Program"),
  }),
});
const { value: addTrainingSelectedPortal } = useField<number | string>(
  "addTrainingSelectedPortal"
);
const { value: addTrainingSelectedProgram } = useField<number | string>(
  "addTrainingSelectedProgram"
);

const addTrainingProgramOptions = computed(() =>
  trainingStore.portalTrainingPrograms.map((tp) => ({
    name: tp.name,
    value: tp.trainingProgramId,
  }))
);

const addSelectedPortalName = computed(
  () =>
    bldgStore.properties.find((p) => p.portalId == addTrainingSelectedPortal.value)?.name ?? ""
);
const addSelectedProgramName = computed(
  () =>
    trainingStore.portalTrainingPrograms.find(
      (tp) => tp.trainingProgramId == addTrainingSelectedProgram.value
    )?.name ?? ""
);

watch(addTrainingSelectedPortal, async (newPortalId) => {
  if (!newPortalId) return;
  addTrainingSelectedProgram.value = "";
  isAddTrainingProgramsLoading.value = true;
  await trainingStore.getTrainingProgramsByPortal(Number(newPortalId));
  isAddTrainingProgramsLoading.value = false;
});

function addTrainingPermissionClick() {
  resetAddTrainingForm();
  trainingStore.portalTrainingPrograms = [];
  addTrainingPermissionError.value = "";
  isAddTrainingPermissionPopupVisible.value = true;
}

const goToAddTrainingConfirm = handleSubmitAddTraining(() => {
  addTrainingPermissionError.value = "";
  addTrainingResultMessage.value = "";
  addTrainingSuccess.value = false;
  isAddTrainingPermissionPopupVisible.value = false;
  isAddTrainingConfirmPopupVisible.value = true;
}, onInvalidSubmitAddTraining);

async function executeAddTrainingPermission() {
  isPopupLoading.value = true;
  const result = await trainingStore.bulkAddTrainingProgramPermission(
    Number(addTrainingSelectedPortal.value),
    Number(addTrainingSelectedProgram.value)
  );
  isPopupLoading.value = false;

  if (result.error) {
    addTrainingResultMessage.value = result.error;
    addTrainingSuccess.value = false;
  } else {
    addTrainingResultMessage.value = `Successfully added ${result.count} permission${result.count !== 1 ? "s" : ""}.`;
    addTrainingSuccess.value = true;
  }
}

function onInvalidSubmitAddTraining() {
  addTrainingPermissionError.value = "One or more invalid form inputs.";
}
// #endregion Add training program permission to portal

function onInvalidSubmit() {
  popupMessage.value = "One or more invalid form inputs.";
}
</script>
